import React, { useCallback, useLayoutEffect, useRef, useState } from 'react'
import { placeTooltip, Viewport } from '../core/tooltip'

type Props = {
  /** Точка привязки в координатах содержимого обёртки графика. */
  anchorX: number
  anchorY: number
  getViewport: () => Viewport
  interactive?: boolean
  zIndex?: number
  onClick?: (e: React.MouseEvent) => void
  children: React.ReactNode
}

/**
 * Всплывашка у точки. Позиция считается по реальному размеру отрисованного
 * содержимого, а не по оценке: подсказка разной высоты при переворачивании
 * вверх или прижатии к краю иначе уезжает от курсора на разницу оценки. Размер
 * перемеряется, когда меняется содержимое (спиннер сменяется карточкой).
 */
const FloatingBox: React.FC<Props> = ({
  anchorX,
  anchorY,
  getViewport,
  interactive = false,
  zIndex = 9,
  onClick,
  children,
}) => {
  const ref = useRef<HTMLDivElement>(null)
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null)

  const place = useCallback(() => {
    const el = ref.current
    if (!el) return
    setPos(
      placeTooltip(
        anchorX,
        anchorY,
        { width: el.offsetWidth, height: el.offsetHeight },
        getViewport()
      )
    )
  }, [anchorX, anchorY, getViewport])

  useLayoutEffect(() => {
    place()
  }, [place])

  useLayoutEffect(() => {
    const el = ref.current
    if (!el || typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(() => place())
    observer.observe(el)
    return () => observer.disconnect()
  }, [place])

  return (
    <div
      ref={ref}
      className='position-absolute'
      style={{
        left: pos?.left ?? 0,
        top: pos?.top ?? 0,
        visibility: pos ? 'visible' : 'hidden',
        pointerEvents: interactive ? 'auto' : 'none',
        zIndex,
      }}
      onClick={onClick}
    >
      {children}
    </div>
  )
}

export default FloatingBox
